import React, { useEffect, useState } from "react";
import { useClientColyseus } from "../../Contexts/ClientColyseusContext";
import FormCreateRoom from "./FormCreateRoom";
import PlayerNamePrompt from "./PlayerNamePrompt";
const PageMultiplayer = () => {
  const { roomsList, getAvailableRooms, joinById } = useClientColyseus();
  const [roomIdInput, setRoomIdInput] = useState("");
  const [playerName, setPlayerName] = useState("");
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [targetRoomId, setTargetRoomId] = useState("");
  const [joinMethod, setJoinMethod] = useState(""); // "id", "room", "create"

  useEffect(() => {
    getAvailableRooms();
    const interval = setInterval(getAvailableRooms, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const handleJoinById = (e) => {
    e.preventDefault();
    if (roomIdInput.trim()) {
      setTargetRoomId(roomIdInput.trim());
      setJoinMethod("id");
      setShowNamePrompt(true);
    }
  };

  const handleJoinRoom = (roomId) => {
    setTargetRoomId(roomId);
    setJoinMethod("room");
    setShowNamePrompt(true);
  };

  const handleCreateRoom = () => {
    setJoinMethod("create");
    setShowNamePrompt(true);
  };

  const handleNameSubmit = (name) => {
    setPlayerName(name);
    setShowNamePrompt(false);
    
    if (joinMethod === "create") {
      setShowCreateForm(true);
    } else {
      // Join existing room with the entered name
      joinById(targetRoomId, { name });
    }
  };

  const handleBackToLobby = () => {
    setShowCreateForm(false);
    setShowNamePrompt(false);
    setTargetRoomId("");
    setJoinMethod("");
    setPlayerName("");
    setRoomIdInput("");
    getAvailableRooms(); // Refresh room list
  };

  // Show name prompt
  if (showNamePrompt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-indigo-800 mb-2">Join Multiplayer</h1>
            <p className="text-gray-600">
              {joinMethod === "create" ? "Enter your name to create a room" : 
               joinMethod === "id" ? `Enter your name to join room: ${targetRoomId}` :
               `Enter your name to join the selected room`}
            </p>
          </div>
          <PlayerNamePrompt onSubmit={handleNameSubmit} />
          <div className="text-center mt-4">
            <button
              onClick={handleBackToLobby}
              className="text-indigo-600 hover:text-indigo-800 underline"
            >
              ← Back to Lobby
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show create room form
  if (showCreateForm) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-indigo-800 mb-2">Create Room</h1>
            <p className="text-gray-600">Welcome, {playerName}! Set up your multiplayer room.</p>
          </div>
          <FormCreateRoom playerName={playerName} onBack={handleBackToLobby} />
        </div>
      </div>
    );
  }

  // Main lobby view
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-indigo-800 mb-2">Multiplayer Lobby</h1>
          <p className="text-gray-600">Join existing rooms or create your own!</p>
        </div>

        {/* Action Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Create Room Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 p-3 rounded-full mr-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Create New Room</h3>
                <p className="text-gray-600">Start a new multiplayer game</p>
              </div>
            </div>
            <button
              onClick={handleCreateRoom}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Create Room
            </button>
          </div>

          {/* Join by Link/ID Card */}
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 p-3 rounded-full mr-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Join by Link</h3>
                <p className="text-gray-600">Paste a link</p>
              </div>
            </div>
            <form onSubmit={handleJoinById} className="space-y-3">
              <input
                type="text"
                value={roomIdInput}
                onChange={(e) => setRoomIdInput(e.target.value)}
                className="w-full border-2 border-gray-300 rounded-lg px-4 py-2 focus:border-blue-500 focus:outline-none"
                placeholder="Paste room link..."
              />
              <button 
                type="submit" 
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                disabled={!roomIdInput.trim()}
              >
                Join Room
              </button>
            </form>
          </div>
        </div>

        {/* Available Rooms */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-indigo-600 text-white px-6 py-4">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold">Available Rooms</h3>
              <button
                onClick={getAvailableRooms}
                className="bg-indigo-500 hover:bg-indigo-400 text-white px-3 py-1 rounded-lg text-sm transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
          
          {roomsList.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <h4 className="text-xl font-semibold text-gray-600 mb-2">No Active Rooms</h4>
              <p className="text-gray-500 mb-4">Be the first to create a room and invite friends!</p>
              <button
                onClick={handleCreateRoom}
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Create First Room
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Players</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {roomsList.map((room, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{room.metadata?.name || room.name || "Unnamed Room"}</div>
                        <div className="text-sm text-gray-500">ID: {room.roomId}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {room.metadata?.course || "Default Course"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          room.metadata?.mode === 'cooperative' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {room.metadata?.mode || 'Competitive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {room.clients || 0}/{room.metadata?.maxPlayers || 8}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleJoinRoom(room.roomId)}
                          className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                        >
                          Join Room
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="text-center mt-8">
          <button
            onClick={() => window.location.href = '/notepad'}
            className="bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default PageMultiplayer;
